import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import ArrowLeft from '../../Assets/Arrow left.svg';
import OrderStats from './Components/OrderStats';
import ProductDetails from './Components/ProductDetails';
import Modal from './Components/Modal/Modal';
import ReturnTimelineMessage from './Components/ReturnTimelineMessage';
// import ReturnRequest from './Components/ReturnRequest';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrderDetails, fetchShipmentData, setModal, setOrderStatus } from '../../Features/orderDetailsSlice';
// import ExchangeRequest from './Components/ExchangeRequest';
import { generateLabel, getOrderInvoice, getOrderStatusById } from '../../lib/utils/apiCalls';
import { showToast } from '../../Components/Toaster/Toaster';
import Loading from '../../Components/Loading/Loading';
import OrderTracker from './Components/OrderTracker/OrderTracker';
import { toast } from 'react-toastify';
import Error from '../../Components/Error/Error';

function OrderDetailsPage() {
    const { orderId } = useParams();
    const dispatch = useDispatch();

    const { orderDetails, loading } = useSelector((state) => state.orderDetails.orderDetails);
    const orderStatus = useSelector((state) => state.orderDetails.orderStatus);

    const location = useLocation();
    const [rejectReason, setRejectReason] = useState('');

    useEffect(() => {
        orderId && dispatch(fetchOrderDetails(orderId));
        orderId && dispatch(fetchShipmentData(orderId));
        // eslint-disable-next-line
    }, [orderId]);

    const handleCancelOrder = () => {
        dispatch(setModal('cancel-order'));
    }

    async function handleDownloadInvoice() {
        try {
            await toast.promise(
                getOrderInvoice(orderDetails.orderId),
                {
                    pending: {
                        render() {
                            return 'Downloading invoice...';
                        },
                        className: 'toast-pending',
                    },
                    success: {
                        render({ data }) {
                            const blob = new Blob([data.data], { type: 'application/pdf' });

                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `invoice-${orderDetails.orderId}.pdf`;

                            document.body.appendChild(link);
                            link.click();

                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);

                            return 'Invoice downloaded.';
                        },
                    },
                    error: {
                        render({ data }) {
                            return `Download failed: ${data.message || 'Something went wrong'}`;
                        },
                    },
                }
            );
        } catch (err) {
            showToast(err, err);
        }
    }

    const generateShippingLabel = async () => {
        if (orderDetails.waybill) {
            try {
                await toast.promise(
                    generateLabel(orderDetails.orderId),
                    {
                        pending: {
                            render() {
                                return 'Generating shipping label...';
                            },
                            className: 'toast-pending',
                        },
                        success: {
                            render({ data }) {
                                let pdf_download_link = data[0].pdf_download_link;
                                window.open(pdf_download_link, '_blank');
                                return 'Shipping label generated.';
                            },
                        },
                        error: {
                            render({ data }) {
                                return `Failed to generate label: ${data.message || 'Something went wrong'}`;
                            },
                        },
                    }
                );
            } catch (err) {
                console.log(err);
            }
        } else {
            showToast("Can't generate label. Tracking id not present.", 'warning');
        }
    }

    useEffect(() => {
        const fetchOrderStatus = async () => {
            try {
                const response = await getOrderStatusById(orderDetails.orderId);
                dispatch(setOrderStatus(response));
            }
            catch(err) {
                console.log(err);
            }
        }

        Object.keys(orderDetails).length > 0 && fetchOrderStatus();
        // eslint-disable-next-line
    }, [orderDetails]);

    if(loading === 'loading') {
        return <Loading />
    }

    if(loading === 'failed') {
        return <Error />
    }

    return (
        <div className="w-full overflow-y-scroll pb-16 outline-none">
            <div className="flex items-center justify-between gap-6 relative m-[20px_19px_24px_20px]">
                <div className="w-fit flex items-center">
                    <Link to={`${location.pathname.split('/').slice(0, -1).join('/')}`} className="img p-[10px] flex items-center justify-center">
                        <img className='w-6 h-6 text-[#666666]' src={ArrowLeft} alt="" />
                    </Link>

                    <h1 className="p-[6px] text-[#121212] font-bold leading-8 text-[24px]">Order Details &#40;#{orderDetails?.orderId?.slice(-6)}&#41;</h1>
                </div>

                <div className="flex gap-4">
                    <button 
                        onClick={handleDownloadInvoice} 
                        className='bg-[#FFFFFF] rounded-md px-5 py-[10px] whitespace-nowrap text-[#121212] font-semibold text-[14px] leading-[22px] outline-none'
                    >
                        Download Invoice
                    </button>

                    {
                        (orderStatus === 'PENDING') &&
                        <button 
                            onClick={handleCancelOrder} 
                            className='bg-rede5 rounded-md px-5 py-[10px] whitespace-nowrap text-[#FFFFFF] font-semibold text-[14px] leading-[22px] outline-none'
                        >
                            Cancel Order
                        </button>
                    }
                </div>
            </div>

            <OrderStats />

            <div className="flex gap-2 mx-5 mt-3">
                <div className="w-3/5 h-fit flex flex-col gap-2">
                    <ProductDetails />
                </div>

                <div className="w-2/5 h-fit flex flex-col gap-2">
                    {/* {
                        (orderStatus === 'CANCELLED' || (orderStatus === 'RETURN')) && 
                        <ReturnTimelineMessage />
                    } */}

                    <OrderTracker title={"Order Timeline"}/>

                    {
                        (orderStatus === 'READY_FOR_PICKUP' || orderStatus === 'PENDING') &&
                        <div className="flex flex-col gap-2">
                            <button onClick={generateShippingLabel} className={`outline-none px-5 py-[10px] mt-4 ${orderDetails.waybill ? 'bg-primaryColor hover:bg-primaryAccent ' : 'bg-buttonDisabledBg cursor-not-allowed'} rounded-md font-semibold text-[14px] leading-[22px] text-buttonText`}>Generate Shipping Label</button>

                            {
                                !orderDetails.waybill && 
                                <p className='text-[12px] leading-6 text-[#666666] font-medium italic select-none'>Can't generate label. Tracking id not present.</p>
                            }
                        </div>
                    }
                </div>
            </div>

            <Modal 
                // rejectReturn={rejectReturn} 
                // rejectExchange={rejectExchange} 
                reasonToReject={rejectReason} 
                updateRejectReason={val => setRejectReason(val)} 
                updateExchangeRejectReason={val => setRejectReason(val)} 
            />
        </div>
    );
}

export default OrderDetailsPage;
